export const LIST_PAGE_SIZES = [20, 50, 100] as const

export type ListPageSize = (typeof LIST_PAGE_SIZES)[number]

export const DEFAULT_LIST_PAGE_SIZE: ListPageSize = 20

export interface PageWindow {
  pages: number[]
  showLeftEllipsis: boolean
  showRightEllipsis: boolean
}

const WINDOW_SIZE = 3

/** Three consecutive page numbers, current page centered when possible. */
export function getPageWindow(currentPage: number, totalPages: number): PageWindow {
  if (totalPages <= 0) {
    return { pages: [], showLeftEllipsis: false, showRightEllipsis: false }
  }
  if (totalPages <= WINDOW_SIZE) {
    return {
      pages: Array.from({ length: totalPages }, (_, i) => i + 1),
      showLeftEllipsis: false,
      showRightEllipsis: false,
    }
  }
  const safeCurrent = Math.min(Math.max(currentPage, 1), totalPages)
  const start = Math.min(Math.max(safeCurrent - 1, 1), totalPages - (WINDOW_SIZE - 1))
  const end = start + (WINDOW_SIZE - 1)
  return {
    pages: Array.from({ length: WINDOW_SIZE }, (_, i) => start + i),
    showLeftEllipsis: start > 1,
    showRightEllipsis: end < totalPages,
  }
}

export interface ListPageRange {
  pageCount: number
  from: number
  to: number
  sliceStart: number
  sliceEnd: number
}

/** 1-based Showing from–to, plus 0-based slice indexes. */
export function getListPageRange(total: number, page: number, pageSize: number): ListPageRange {
  if (total <= 0 || pageSize <= 0) {
    return { pageCount: 0, from: 0, to: 0, sliceStart: 0, sliceEnd: 0 }
  }
  const pageCount = Math.ceil(total / pageSize)
  const safePage = Math.min(Math.max(page, 1), pageCount)
  const sliceStart = (safePage - 1) * pageSize
  const sliceEnd = Math.min(sliceStart + pageSize, total)
  return {
    pageCount,
    from: sliceStart + 1,
    to: sliceEnd,
    sliceStart,
    sliceEnd,
  }
}

export function pageAfterPageSizeChange(
  currentPage: number,
  totalItems: number,
  newPageSize: number,
): number {
  if (totalItems <= 0 || newPageSize <= 0) return 1
  const pageCount = Math.max(1, Math.ceil(totalItems / newPageSize))
  return Math.min(Math.max(currentPage, 1), pageCount)
}

export function isListPageSize(value: number): value is ListPageSize {
  return (LIST_PAGE_SIZES as readonly number[]).includes(value)
}
